import {MYPICKS} from 'constants/actions';
import {ActionType} from 'constants/flow/flowTypes';
import {without} from 'ramda';

import {put, takeLatest, call, select, all} from 'redux-saga/effects';
import {sendEvent} from '@optimaxdev/analytics/mobile';
import {wishlist, reportChangeWishlist} from 'libs/wishlist/wishlist';
import {localStore} from 'libs/storage/storage';
import {
    getWishlistIds,
    setWishlistIds,
    isOverLimit,
    setPopupVisibility,
} from 'reducers/wishlist/wishlist';
import {getActiveProduct} from 'selectors/product/product';
import {getWindowProduct} from 'selectors/category/category';
import {isCustomerLogged} from 'selectors/customer/customer';
import {
    isCategoryPage as isCategoryPageSelector,
    isProductPage as isProductPageSelector,
} from 'selectors/routerInfo/routerInfo';
import {POST_MESSAGE_TYPES, postMessage} from 'libs/postMessage/postMessage';

export const WISHLIST_STORE_KEY = 'wishlist';

type ToggleItemPropsType = {
    productId: number;
    source?: string;
};

/**
 * Updates the count of wishlist items in the header
 * @param {unknown} error error
 * @param {number} count number of items
 */
export function* updateWishlistItemsCount(error: Maybe<unknown>, count: number) {
    if (!error)
        yield call(postMessage, {
            type: POST_MESSAGE_TYPES.WISHLIST_UPDATE_ITEMS_COUNT,
            data: count,
        });
}

/**
 * Report analytics about a change in the wishlist for a category page or a product page
 * @param {boolean} isAdded - new product is added
 * @param {boolean} isOver - exceeding the limit of added products
 */
export function* reportChangeWishlistForMultiplePages(isAdded = true, isOver = false) {
    const isCategoryPage = yield select(isCategoryPageSelector);
    const isProductPage = yield select(isProductPageSelector);
    if (isCategoryPage || isProductPage)
        yield call(reportChangeWishlist, isProductPage, isAdded, isOver);
}

/**
 * Adds local items to user's wishlist
 * @param {number[]} addItems item ids to add
 */
export function* addLocalItemsToWishlist(addItems: number[]) {
    if (addItems.length) {
        const [error] = yield call([wishlist, 'addMultiple'], addItems);
        yield call(updateWishlistItemsCount, error, addItems.length);
    }
}

/**
 * Fetch wishlist from server
 * @param {number[]} localItems - items from localStorage
 * @returns {number[]} combined localItems and items from server if there was no server error
 */
function* getItemsFromServer(localItems: number[]) {
    const [error, userItems] = yield call([wishlist, 'get']);
    let items = [...localItems];
    if (!error) {
        yield call([localStore, 'remove'], WISHLIST_STORE_KEY);

        const addItems = without(userItems, items);
        yield call(addLocalItemsToWishlist, addItems);
        items = [...addItems, ...userItems];
    }
    return items;
}

/**
 * Sets wishlist
 * @param {boolean} isLogged - flag if user is logged in
 */
export function* setWishlist(isLogged: boolean) {
    let items = yield call([localStore, 'get'], WISHLIST_STORE_KEY, []);
    if (isLogged) {
        items = yield call(getItemsFromServer, items);
    } else {
        yield call([localStore, 'set'], WISHLIST_STORE_KEY, items);
    }

    yield put(setWishlistIds(items));
}

/**
 * Add my picks
 * @param {number} id item id
 */
export function* addToWishlist(id: number) {
    const [ids, isLogged] = yield all([select(getWishlistIds), select(isCustomerLogged)]);
    const newIds = [...ids, id];
    yield put(setWishlistIds(newIds));

    if (isLogged) {
        const [error] = yield call([wishlist, 'add'], id);
        yield call(updateWishlistItemsCount, error, newIds.length);
    } else yield call([localStore, 'set'], WISHLIST_STORE_KEY, newIds);
}

/**
 * Add mypicks or show popup
 * @param {number} id - product id
 */
export function* addInWishlistOrShowPopup(id: number) {
    const isOver = yield select(isOverLimit);
    yield call(reportChangeWishlistForMultiplePages, true, isOver);
    if (isOver) {
        yield put(setPopupVisibility(true));
    } else {
        yield call(addToWishlist, id);
    }
}

/**
 * Send add-to-wish-list DY event
 * @param {number} id - product id
 */
export function* onAddToWishListAnalytics(id: number) {
    const [productPDP, productCTP] = yield all([
        select(getActiveProduct),
        select(getWindowProduct, id),
    ]);

    if (!productPDP && !productCTP) return;

    const data = {
        name: 'Add to Wishlist',
        properties: {
            dyType: 'add-to-wishlist-v1',
            productId: productPDP ? productPDP.sku : productCTP.sku,
            size: productPDP ? productPDP.frame.size.label : productCTP.frame.size.label,
        },
    };

    sendEvent('event', data);
}

/**
 * Removes an item from the wishlist
 * @param {number[]} ids current wishlist items
 * @param {number} productId item to remove
 */
export function* removeFromWishlist(ids: number[], productId: number) {
    const isLogged = yield select(isCustomerLogged);
    const newIds = ids.filter(itemId => itemId !== productId);
    yield put(setWishlistIds(newIds));
    yield call(reportChangeWishlistForMultiplePages, false);
    if (isLogged) {
        const [error] = yield call([wishlist, 'remove'], productId);
        yield call(updateWishlistItemsCount, error, newIds.length);
    } else yield call([localStore, 'set'], WISHLIST_STORE_KEY, newIds);
}

/**
 * Toggle wishlist item
 * @param {ActionType<number>} action with product id
 */
export function* toggleItem({payload: {productId}}: ActionType<ToggleItemPropsType>) {
    const ids = yield select(getWishlistIds);

    if (ids.includes(productId)) {
        yield call(removeFromWishlist, ids, productId);
        yield call(postMessage, {
            type: POST_MESSAGE_TYPES.WISHLIST_UPDATE,
            data: {productId, in: false},
        });
    } else {
        yield all([
            call(onAddToWishListAnalytics, productId),
            call(addInWishlistOrShowPopup, productId),
        ]);
        yield call(postMessage, {
            type: POST_MESSAGE_TYPES.WISHLIST_UPDATE,
            data: {productId, in: true},
        });
    }
}
/**
 * Begin of saga
 */
export default function* myPicksSaga() {
    yield takeLatest(MYPICKS.TOGGLE, toggleItem);
}
